import { useState, useEffect } from "react";
import { Breadcrumb } from "react-bootstrap";
import { useSelector } from "react-redux";
import { NavLink, useHistory, useParams } from "react-router-dom";

import bankAccountApi from "../../api/bankAccount";
import { useProfileListByType, UserProfile } from "../../api/profile";
import { Loader, toast } from "../../common/components/core";
import { currentRolesSelector } from "../../redux/slices/auth/selectors";
import { FormSubmissionHandler, BankAccount } from "../../types/common";
import { SuperAdminRole, superAdminAccountType } from "../../types/superadmin";
import { isNodeDeveloper } from "../../utils/role";
import { PageTitle } from "../../types/pageTitle";

import AccountForm, {
    AccountFormValues,
    parse2ProfileDetails,
    addr2Sub,
} from "./components/AccountForm";
import useAccount from "./hooks/useAccount";

export enum ProfileType {
    ALL = "All_PROFILES",
    DEVELOPER = "DEVELOPER",
    APPROVER = "APPROVER",
    HSBC = "HSBC",
    MAIN_CONTRACTOR = "MAIN_CONTRACTOR",
}

const EditAccountView = (): JSX.Element => {
    document.title = PageTitle.EditAccountView;
    const history = useHistory();
    const currentRoles = useSelector(currentRolesSelector);
    const accountType = superAdminAccountType(currentRoles);
    const isDeveloper = isNodeDeveloper();
    const currentProListType = () => {
        if(isDeveloper) {
            return ProfileType.DEVELOPER;
        } else {
            return ProfileType.MAIN_CONTRACTOR;
        }
    }
    const { data: profileNameList = [] } = useProfileListByType(currentProListType());

    const [isSubmitting, setSubmitting] = useState(false);
    const { accountId } = useParams<{ accountId: string }>();
    const { bankAccount, isValidating, mutate } = useAccount(accountId);

    useEffect(() => {
        return () => {
            setSubmitting(false);
        };
    }, []);
    const handleSubmit: FormSubmissionHandler<AccountFormValues> = async (
        values,
    ) => {
        if (!values || !bankAccount?.id) {
            return;
        }

        setSubmitting(true);
        try {
            await bankAccountApi.update(bankAccount.id, values);
            mutate();
            history.push(`/account-setup/bank-account-details/${accountId}`);
            toast("Bank Account Updated");
        } catch (e) {
            console.error(e);
            mutate();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Loader isLoading={isValidating || isSubmitting}>
            {accountType && bankAccount && (
                <PureEditAccountView
                    profileNameList={profileNameList}
                    accountType={accountType}
                    account={bankAccount}
                    currencies={["SGD", "USD", "CNY"]}
                    onSubmit={handleSubmit}
                    onCancel={() => history.push("/account-setup")}
                />
            )}
        </Loader>

    );
};

type PureEditAccountViewProps = {
    accountType: SuperAdminRole;
    account: BankAccount,
    currencies: string[];
    onSubmit: FormSubmissionHandler<AccountFormValues>;
    onCancel: () => void;
    profileNameList: UserProfile[];
};

export const PureEditAccountView = ({
    accountType,
    account,
    currencies,
    onSubmit,
    onCancel,
    profileNameList,
}: PureEditAccountViewProps): JSX.Element => {
    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/account-setup">{`${accountType} Account Setup`}</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    {`Edit ${accountType} Bank Account`}
                </Breadcrumb.Item>
            </Breadcrumb>
            <AccountForm
                profileNameList={profileNameList}
                accountType={accountType}
                account={account}
                currencies={currencies}
                onCancel={onCancel}
                onSubmit={onSubmit}
                isEdit={true}
                initialValues={{
                    holderName: account.bankAccountHolderName,
                    accountNumber: account.bankAccountNumber,
                    swiftAddress: account.swiftAddress,
                    bankName: account.bankName,
                    registeredAddress: addr2Sub(account.registeredAddress, "addr"),
                    city: addr2Sub(account.registeredAddress, "city") ? addr2Sub(account.registeredAddress, "city") : "Singapore",
                    countryCode: addr2Sub(account.registeredAddress, "code") ? addr2Sub(account.registeredAddress, "code") : "SG",
                    paymentCurrency: account.currency,
                    profile: parse2ProfileDetails(account.userProfile),
                }}
            />
        </>
    );
};

export default EditAccountView;
